import { Badge, Button, Card, CardBody, EmptyState, Kpi, PageHeader, Skeleton } from '@bifeai/ui';
import { AlarmClock, HandCoins, UserPlus, Users } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { CustomerFormDialog } from '../../components/customers/CustomerFormDialog';
import { SearchBox, Toolbar } from '../../components/inventory';
import { useCustomers, type CustomerFilter } from '../../lib/customers';
import { capitalize, formatMoney, timeAgo } from '../../lib/format';
import { FranchisePrivate } from '../../components/FranchisePrivate';
import { useSession } from '../../lib/session';

const FILTERS: { key: CustomerFilter | 'archived'; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'debt', label: 'Deben' },
  { key: 'overdue', label: 'Deuda atrasada' },
  { key: 'archived', label: 'Archivados' },
];

export function CustomersPage() {
  const { privateFranchise } = useSession();
  if (privateFranchise) return <FranchisePrivate title="Clientes" what="Los clientes" />;
  return <CustomersPageContent />;
}

function CustomersPageContent() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<CustomerFilter | 'archived'>('all');
  const [creating, setCreating] = useState(false);
  const list = useCustomers(search, filter === 'archived' ? 'all' : filter, filter === 'archived');
  const debtors = useCustomers('', 'debt');
  const debt = debtors.data ?? [];
  const total = debt.reduce((a, c) => a + c.balanceCents, 0);
  const overdue = debt.filter((c) => c.overdue);
  const rows = list.data ?? [];

  return (
    <>
      <PageHeader
        title="Clientes y fiado"
        subtitle="Quién te debe, cuánto y desde cuándo. Tocá un cliente para cobrarle, ver su cuenta o mandarle el saldo por WhatsApp."
        actions={
          <Button variant="primary" icon={<UserPlus />} onClick={() => setCreating(true)}>
            Nuevo cliente
          </Button>
        }
      />
      <div className="bf-kpi-grid">
        <Kpi
          label="Total a cobrar"
          icon={<HandCoins />}
          tone="red"
          value={formatMoney(total)}
          delta={`${debt.length} ${debt.length === 1 ? 'cliente debe' : 'clientes deben'}`}
          deltaMuted
        />
        <Kpi
          label="Deuda atrasada"
          icon={<AlarmClock />}
          tone="amber"
          value={formatMoney(overdue.reduce((a, c) => a + c.balanceCents, 0))}
          delta={overdue.length ? `${overdue.length} sin pagar hace tiempo` : 'Nadie atrasado'}
          deltaMuted
        />
      </div>
      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Buscar por nombre, teléfono o DNI" />
        <div className="bf-chips" role="group" aria-label="Filtro">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" className="bf-chip" aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
              {f.label}
            </button>
          ))}
        </div>
      </Toolbar>
      <Card>
        <CardBody flush>
          {list.isPending ? (
            <div style={{ padding: '8px 22px 16px', display: 'grid', gap: 12 }}>
              <Skeleton />
              <Skeleton />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Users />}
              title={search ? 'No encontramos clientes' : filter === 'all' ? 'Todavía no hay clientes' : 'No hay clientes en este filtro'}
              text={filter === 'all' && !search ? 'Se cargan acá o desde el mostrador, al cobrar con cuenta corriente.' : undefined}
            />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th style={{ textAlign: 'right' }}>Debe</th>
                    <th>Límite de fiado</th>
                    <th>Último movimiento</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.id} data-clickable="true" onClick={() => navigate(`/clientes/${c.id}`)}>
                      <td>
                        <span className="bf-cell-main">{c.name}</span> {c.overdue && <Badge tone="warning">Atrasada</Badge>}
                        <div className="bf-cell-sub">{[c.phone, c.docNumber].filter(Boolean).join(' · ') || '—'}</div>
                      </td>
                      <td
                        className={`bf-num ${c.balanceCents > 0 ? 'bf-text-danger' : 'bf-text-muted'}`}
                        style={{ textAlign: 'right', fontWeight: 600 }}
                      >
                        {c.balanceCents > 0 ? formatMoney(c.balanceCents) : c.balanceCents < 0 ? `A favor ${formatMoney(-c.balanceCents)}` : 'Al día'}
                      </td>
                      <td className="bf-num bf-cell-sub">{c.creditLimitCents > 0 ? formatMoney(c.creditLimitCents) : 'Sin fiado'}</td>
                      <td className="bf-cell-sub">{c.lastMovementAt ? capitalize(timeAgo(c.lastMovementAt)) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      {creating && (
        <CustomerFormDialog
          onClose={() => setCreating(false)}
          onSaved={(c) => {
            setCreating(false);
            navigate(`/clientes/${c.id}`);
          }}
        />
      )}
    </>
  );
}
